import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import PublicHeader from '../components/PublicHeader';
import { EarlyAccessForm } from '../components/EarlyAccessForm';
import { useLaunchStatus } from '../lib/launchStatus';
import AppFooter from '../components/AppFooter';
import RecordScrollShowcase from '../components/RecordScrollShowcase';
import SecurityLockAnimation from '../components/SecurityLockAnimation';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';

const STEPS = [
  {
    title: 'Tell us what you know',
    body: "The places you lived, the people who looked after you, the schools and doctors you remember. Rough dates and half-remembered names are fine. This is your life, not a test you can fail.",
  },
  {
    title: 'We ask on your behalf',
    body: "Every organisation you list gets a formal Subject Access Request. We track the legal one-month deadline for each one, chase anyone who's late, and keep asking until they've actually answered.",
  },
  {
    title: 'Read what comes back',
    body: "Replies land in one place as they arrive, encrypted so only you can open them. Nothing to install and nothing to organise yourself. Just read about your own life as it comes in.",
  },
  {
    title: 'Understand the full story',
    body: "Our tools pull every reply into one timeline so you can see how decisions connect and why they were made. They flag gaps and anything blacked out, and point you to support if something difficult comes up.",
  },
];

const FAQS = [
  {
    q: 'What is a Subject Access Request?',
    a: "It's a formal legal request that makes an organisation show you every record it holds about you. Councils, schools, GPs and social workers all have to respond within a month by law. We write and send these requests for you.",
  },
  {
    q: 'Can I request my social worker’s records?',
    a: 'Yes. Any council or social work team that had a role in your care has to release what they hold about you, including social worker notes, review minutes and case files.',
  },
  {
    q: 'I was in foster care. What records can I get?',
    a: 'You can request records from your local authority, any foster agency involved, the schools you attended, and health services you saw. We help you list every organisation that might hold something.',
  },
  {
    q: 'I was adopted. Does this still apply to me?',
    a: 'Yes. Adoption agencies, local authorities and courts often hold detailed records from before and during an adoption. You have a legal right to request them, whatever your relationship with your birth family looks like now.',
  },
  {
    q: 'Can I see my file if I’m still in care?',
    a: 'Yes, at any age. If you’re under 18, a social worker or an advocate can help you make the request, but it’s still your file and your right, not something you need anyone’s permission for.',
  },
  {
    q: 'How do I find out why I was taken into care?',
    a: 'That’s usually written down in your social work file, often in the first assessment or court report. Requesting records from your local authority is the most direct way to see the reasons recorded at the time.',
  },
  {
    q: 'I don’t remember which council looked after me. What do I do?',
    a: 'Tell us what you remember, a town, a school, roughly how old you were, and we’ll help you work out who to ask. You don’t need the exact name of a council or team to get started.',
  },
  {
    q: 'What’s a life story book? Is it the same as this?',
    a: 'A life story book is usually a shorter account a social worker put together for you. Your full file often holds far more than that. Precious Data helps you request everything, not just the summary.',
  },
  {
    q: 'How long does a Subject Access Request take?',
    a: 'Organisations have one calendar month to respond by law, though they can extend by up to two more months for a complex request. We track every deadline and follow up automatically if anyone runs late.',
  },
  {
    q: 'Is Precious Data free?',
    a: "Yes, it's free for care leavers to use. We're funded by donations and organisations who believe you shouldn't have to pay to understand your own life.",
  },
  {
    q: 'Can I let a solicitor or therapist see my records?',
    a: 'Yes. Your data is encrypted so only you can open it by default, but you can invite someone you trust, a solicitor, a therapist, a support worker, to access it with you whenever you choose.',
  },
  {
    q: 'What if an organisation refuses or ignores my request?',
    a: "We chase them for you. If they still don't respond, we help you escalate to the Information Commissioner's Office, who can require them to comply.",
  },
];

const FAQ_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

export const Landing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { comingSoon } = useLaunchStatus();

  // PublicHeader links to "/#how-it-works" from every page, including this
  // one — a same-page anchor click doesn't trigger a route change React
  // Router would notice, so scroll manually whenever the hash matches a
  // section here (covers both landing on this page with the hash already
  // set, and clicking the link while already on it).
  useEffect(() => {
    if (location.hash === '#how-it-works') {
      document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-surface text-ink">
      <script type="application/ld+json">{JSON.stringify(FAQ_JSON_LD)}</script>
      <PublicHeader />

      {/* Hero */}
      <section className="mx-2.5 mb-2.5 rounded-2xl bg-ground px-7 py-11 lg:px-12 lg:py-14">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-center">
          <div>
            <span className="inline-block rounded-full bg-brand-wash text-brand-ink px-4 py-2 text-[15px] font-medium">
              {comingSoon ? 'Coming soon · built with care leavers' : 'Free · confidential · built with care leavers'}
            </span>
            <h1 className="font-heading text-[40px] sm:text-[52px] font-bold tracking-[-0.04em] leading-[0.98] mt-7">
              Councils kept a file on your childhood.
            </h1>
            <p className="text-[19px] leading-[1.5] text-ink-70 mt-6 max-w-[34ch]">
              You have a legal right to see it. We help you send a Subject Access Request to
              every council, school, GP and social worker who might hold a record, whether you
              were in foster care, adopted, or grew up in a children's home. When the replies
              come in, we bring them together into one timeline so you can understand what
              happened in your life and why.
            </p>
            {comingSoon ? (
              <div className="mt-8">
                <p className="text-[15px] text-ink-55 max-w-[34ch]">
                  We're inviting people in gradually while we finish testing. Leave your email and
                  we'll let you know when it's your turn.
                </p>
                <EarlyAccessForm className="mt-4 max-w-sm" testIdPrefix="hero-early-access" />
              </div>
            ) : (
              <div className="mt-8">
                <Button
                  size="lg"
                  onClick={() => navigate('/register')}
                  className="w-full sm:w-auto"
                  data-testid="hero-get-started-btn"
                >
                  See what&apos;s held about you
                </Button>
                <p className="text-[15px] text-ink-55 mt-3">About 10 minutes to set up</p>
              </div>
            )}
          </div>

          <RecordScrollShowcase />
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="px-7 lg:px-12 py-16 md:py-20">
        <div className="max-w-6xl mx-auto">
          <p className="pd-eyebrow">How it works</p>
          <h2 className="font-heading text-[34px] font-bold tracking-[-0.035em] leading-[1.05] mt-3 max-w-[22ch]">
            Four steps. Tell us what you know, we help you understand the rest.
          </h2>
          <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 mt-12">
            {STEPS.map((step, i) => (
              <li key={step.title} className="flex flex-col">
                <span className="font-heading text-[42px] font-bold tracking-[-0.035em] leading-none text-quiet" aria-hidden="true">
                  {i + 1}
                </span>
                <h3 className="font-heading text-[22px] font-semibold tracking-[-0.02em] mt-4">
                  <span className="sr-only">Step {i + 1}: </span>
                  {step.title}
                </h3>
                <p className="text-ink-70 mt-2">{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Locked / trust */}
      <section className="mx-2.5 mb-2.5 rounded-2xl bg-ground px-7 py-12 lg:px-12">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-center">
          <div>
            <p className="pd-eyebrow">Just for you</p>
            <h2 className="font-heading text-[34px] font-bold tracking-[-0.035em] leading-[1.05] mt-3">
              Everyone else used to know your story. Now only you do.
            </h2>
            <div className="space-y-4 text-ink-70 mt-6">
              <p>
                Growing up in care, your life was written down by other people and passed
                between offices you never saw inside. Every record here is encrypted end to
                end the moment it reaches us. Not the organisation who wrote it, not us,
                nobody. Only you hold the key that opens it.
              </p>
              <p>
                When you want support, you can invite someone in: a solicitor, a therapist, a
                support worker, anyone you trust to help you understand what you find or act
                on it. They see only what you choose to share, only when you choose to share
                it.
              </p>
            </div>
          </div>

          <SecurityLockAnimation />
        </div>
      </section>

      {/* FAQ */}
      <section className="px-7 lg:px-12 py-16 md:py-20">
        <div className="max-w-6xl mx-auto">
          <p className="pd-eyebrow">Common questions</p>
          <h2 className="font-heading text-[34px] font-bold tracking-[-0.035em] leading-[1.05] mt-3 max-w-[26ch]">
            Questions people ask before they start.
          </h2>
          <div className="grid md:grid-cols-2 gap-x-12 mt-10">
            {[FAQS.slice(0, 6), FAQS.slice(6)].map((column, colIndex) => (
              <Accordion key={colIndex} type="single" collapsible className="w-full">
                {column.map((faq, i) => (
                  <AccordionItem key={faq.q} value={`faq-${colIndex}-${i}`} className="border-rule">
                    <AccordionTrigger className="text-left font-heading text-[17px] font-semibold text-ink py-5 hover:no-underline">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-ink-70 text-[15px] leading-relaxed pb-5">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ))}
          </div>
        </div>
      </section>

      <AppFooter />
    </div>
  );
};

export default Landing;
